import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Loader } from "lucide-react";
import { Button } from "@/taskpane/components/ui/button";
import { FieldGroup } from "@/taskpane/components/ui/field";
import useLogin from "@/hooks/useLogin";
import { useAuth } from "@/providers/AuthProvider";
import { loginSchema, type LoginFormData } from "@/schemas/auth.schemas";
import OnboardingHeader from "../OnboardingHeader";
import MarketingInfo from "./MarketingInfo";
import TwoFactorDialog from "./TwoFactorDialog";
import { EmailField, PasswordField } from "./LoginFields";

interface LoginProps {
  onShowForgotPassword: () => void;
}

const SubmitButton: React.FC<{
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
}> = ({ isPending, isSuccess, isError }) => (
  <Button
    type="submit"
    className="mt-2"
    size={"lg"}
    disabled={isPending}
    aria-describedby={isError ? "login-error" : undefined}
  >
    {isPending ? (
      <>
        <Loader className="animate-spin" />
        Signing in...
      </>
    ) : isSuccess ? (
      <>
        <Check />
        Signed in
      </>
    ) : (
      "Sign In"
    )}
  </Button>
);

const ForgotPasswordButton: React.FC<{ onShowForgotPassword: () => void }> = ({
  onShowForgotPassword,
}) => (
  <Button
    variant={"link"}
    type="button"
    className="text-primary-300 justify-start p-0 h-auto leading-5 self-start"
    onClick={(e) => {
      e.preventDefault();
      onShowForgotPassword();
    }}
  >
    Forgot Password?
  </Button>
);

const LoginFooter: React.FC = () => (
  <>
    <p className="text-xs text-center text-neutral-400 mt-2">
      By signing in you accept our{" "}
      <a
        href="https://legalassistant.au/terms-and-conditions/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary-300 hover:underline"
      >
        terms and conditions
      </a>
      .
    </p>

    <div className="flex flex-col items-center gap-3 mt-6">
      <p className="text-sm text-neutral-400">Don&apos;t have an account?</p>
      <Button
        type="button"
        className="w-full bg-success-200 hover:bg-success-200/90 text-white font-medium"
        size={"lg"}
        onClick={() => window.open(`${process.env.APP_FRONTEND_URL}/free-trial`, "_blank")}
      >
        Start Free Trial
      </Button>
    </div>
    <MarketingInfo />
  </>
);

const LoginHeader: React.FC = () => (
  <div>
    <h2 id="login-heading" className="text-xl font-medium text-center m-0 block mb-2">
      Sign In
    </h2>
    <p
      id="login-description"
      className="text-sm font-normal text-neutral-400 text-center mb-4 block"
    >
      Enter your credentials to access
      <br />
      {"AI Legal Assistant"}
    </p>
  </div>
);

interface LoginFormViewProps {
  control: ReturnType<typeof useForm<LoginFormData>>["control"];
  handleSubmit: ReturnType<typeof useForm<LoginFormData>>["handleSubmit"];
  onSubmit: (data: LoginFormData) => void;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  showPassword: boolean;
  onTogglePassword: () => void;
  onShowForgotPassword: () => void;
}

const LoginFormView: React.FC<LoginFormViewProps> = ({
  control,
  handleSubmit,
  onSubmit,
  isPending,
  isSuccess,
  isError,
  showPassword,
  onTogglePassword,
  onShowForgotPassword,
}) => (
  <form
    onSubmit={handleSubmit(onSubmit)}
    className="flex flex-col gap-3 w-full"
    noValidate
    aria-label="Sign in form"
  >
    <FieldGroup className="gap-4">
      <EmailField control={control} isPending={isPending} />
      <PasswordField
        control={control}
        isPending={isPending}
        showPassword={showPassword}
        onToggleVisibility={onTogglePassword}
      />
    </FieldGroup>
    <ForgotPasswordButton onShowForgotPassword={onShowForgotPassword} />
    <SubmitButton isPending={isPending} isSuccess={isSuccess} isError={isError} />
    <LoginFooter />
  </form>
);

const Login: React.FC<LoginProps> = ({ onShowForgotPassword }) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [pendingToken, setPendingToken] = React.useState<string | null>(null);
  const loginMutation = useLogin();
  const { isAuthenticated } = useAuth();

  const { control, handleSubmit } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate(
      { username: data.email, password: data.password, is_addon: true },
      {
        onSuccess: (response) => {
          if (response.requires_2fa === true && response.access_token.trim() !== "") {
            // Reset the mutation first so the SubmitButton never flashes
            // "Signed in" while the 2FA dialog is about to open.
            loginMutation.reset();
            setPendingToken(response.access_token);
          }
        },
      }
    );
  };

  // Closes the 2FA dialog and resets the login mutation so the form is fresh.
  // Reused by manual dismiss and by 401 from the verify endpoint (5-min TTL).
  const closeAndReset = () => {
    setPendingToken(null);
    loginMutation.reset();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) closeAndReset();
  };

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 w-full max-w-md mx-auto">
      <OnboardingHeader />
      <p className="font-medium text-sm text-center text-neutral-400 m-0">
        The Gold Standard in Legal Productivity.
      </p>
      <section
        className="flex flex-col items-center gap-y-4 w-full"
        role="main"
        aria-labelledby="login-heading"
        aria-describedby="login-description"
      >
        <LoginHeader />
        <LoginFormView
          control={control}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          isPending={loginMutation.isPending}
          isSuccess={loginMutation.isSuccess && pendingToken === null}
          isError={loginMutation.isError}
          showPassword={showPassword}
          onTogglePassword={() => setShowPassword(!showPassword)}
          onShowForgotPassword={onShowForgotPassword}
        />
      </section>
      {pendingToken !== null && (
        <TwoFactorDialog
          open={true}
          pendingAccessToken={pendingToken}
          onOpenChange={handleDialogOpenChange}
          onVerified={() => setPendingToken(null)}
          onExpired={closeAndReset}
        />
      )}
    </div>
  );
};

export default Login;
